import {
    activityRecordTypeForGoalMetricItemLinkKind,
    marketingGoalDrillItemLimit,
    OutcomeGoalMetric,
    RegionId,
    UserId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import {
    ActivityRowBody,
    activityRowIconClasses,
    ActivityRowParentLink,
} from "components/details-page-v-next/activity-rows";
import { trpc } from "components/rpc/trpc";
import React from "react";
import { GoalDateRange } from "./date-range";
import { DrillRecords } from "./drill-records";
import { outcomeGoalMetricIcons } from "./goal-column-icons";
import { goalMetricItemHref } from "./goal-metric-item";

type OutcomeItem =
    inferRouterOutputs<AppRouter>["marketingGoal"]["myOutcomeItems"][number];

const outcomeDateVerbs: Record<OutcomeGoalMetric, string> = {
    referrals: "Received",
    careStarts: "Care started",
    movesCompleted: "Moved",
};

const outcomeEmptyLabels: Record<OutcomeGoalMetric, string> = {
    referrals: "No referrals received",
    careStarts: "No care started",
    movesCompleted: "No moves completed",
};

const outcomeDateLabel = (date: string): string =>
    new Date(`${date}T00:00:00.000Z`).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
    });

const outcomeItemTitle = (item: OutcomeItem): React.ReactNode => {
    if (item.redaction === "otherOffice") {
        return <span className="text-xs italic text-gray-500">In another office</span>;
    }
    if (item.link && item.name) {
        return (
            <ActivityRowParentLink
                href={goalMetricItemHref(item.link)}
                parentName={item.name}
                recordType={activityRecordTypeForGoalMetricItemLinkKind[item.link.kind]}
            />
        );
    }
    return <span className="font-medium text-gray-900">{item.name}</span>;
};

const OutcomeItemRow: React.FC<{ metric: OutcomeGoalMetric; item: OutcomeItem }> = ({
    metric,
    item,
}) => {
    const Icon = outcomeGoalMetricIcons[metric];
    return (
        <ActivityRowBody
            leading={<Icon aria-hidden="true" className={activityRowIconClasses} />}
            actions={null}>
            {outcomeItemTitle(item)}
            {item.source ? (
                <p className="text-xs text-gray-500">via {item.source}</p>
            ) : null}
            <p className="mt-0.5 text-xs text-gray-500">
                {outcomeDateVerbs[metric]} {outcomeDateLabel(item.date)}
            </p>
        </ActivityRowBody>
    );
};

interface OutcomeRecordsProps {
    metric: OutcomeGoalMetric;
    items: OutcomeItem[] | undefined;
    isLoading: boolean;
    isError: boolean;
    emptyMessage: string;
    onClear: () => void;
}

const OutcomeRecords: React.FC<OutcomeRecordsProps> = ({
    metric,
    items,
    isLoading,
    isError,
    emptyMessage,
    onClear,
}) => {
    const atCap = (items?.length ?? 0) >= marketingGoalDrillItemLimit;
    return (
        <DrillRecords
            resetKey={metric}
            noun="record"
            nounPlural="records"
            items={items}
            isLoading={isLoading}
            isError={isError}
            emptyMessage={emptyMessage}
            atCap={atCap}
            capNote={
                atCap
                    ? `Showing the ${marketingGoalDrillItemLimit} most recent records.`
                    : null
            }
            keyOf={item => item.id}
            renderRow={item => <OutcomeItemRow metric={metric} item={item} />}
            onClear={onClear}
            testId="goal-outcome-records"
        />
    );
};

interface OutcomeRecordsForRangeProps {
    regionId: RegionId;
    marketerUserId: UserId;
    range: GoalDateRange;
    metric: OutcomeGoalMetric;
    onClear: () => void;
}

export const OutcomeRecordsForRange: React.FC<OutcomeRecordsForRangeProps> = ({
    regionId,
    marketerUserId,
    range,
    metric,
    onClear,
}) => {
    const trpcClient = trpc.useTRPC();
    const query = useQuery({
        ...trpcClient.marketingGoal.outcomeItemsForRange.queryOptions({
            regionId,
            marketerUserId,
            startDate: range.start.toString(),
            endDate: range.end.toString(),
            metric,
        }),
    });
    return (
        <OutcomeRecords
            metric={metric}
            items={query.data}
            isLoading={query.isLoading}
            isError={query.isError}
            emptyMessage={`${outcomeEmptyLabels[metric]} in this period.`}
            onClear={onClear}
        />
    );
};

interface MyOutcomeRecordsProps {
    regionId: RegionId;
    month: string;
    metric: OutcomeGoalMetric;
    onClear: () => void;
}

export const MyOutcomeRecords: React.FC<MyOutcomeRecordsProps> = ({
    regionId,
    month,
    metric,
    onClear,
}) => {
    const trpcClient = trpc.useTRPC();
    const query = useQuery({
        ...trpcClient.marketingGoal.myOutcomeItems.queryOptions({
            regionId,
            month,
            metric,
        }),
    });
    return (
        <OutcomeRecords
            metric={metric}
            items={query.data}
            isLoading={query.isLoading}
            isError={query.isError}
            emptyMessage={`${outcomeEmptyLabels[metric]} this month.`}
            onClear={onClear}
        />
    );
};
